import { Subject } from 'rxjs'
import { first, map } from 'rxjs/operators'
import { faCircleNotch, faBarcode, faLockOpen, faLock } from '@fortawesome/free-solid-svg-icons'
import { Config } from 'final-form'
import { Component, Watch } from 'vue-property-decorator'

import { MovementTypes, Container, ContainerStatus } from '@skinsoft/types'
import { ContainerDataStore } from '@skinsoft/entity'
import { containerSelectors } from '@skinsoft/entity-ui'
import { ReactiveVue } from '@skinsoft/reactive-vue'
import { BarcodeReader, Field, Form } from '../../../../ui'
import { AllItemDisplayForm } from '../../all-item-display-form'

export interface Values {
    location: string
    identifier: string
}

declare var ITEM_SEARCH_TYPES: string[]

@Component({
    components: {
        allItemDisplayForm: AllItemDisplayForm
    },
    props: {
        onSubmit: () => {}, 
        initialValues: Object, 
        movementType: String, 
        canChainAdd: Boolean, 
        statusesToSearch: {
            type: Array,
            required: false,
        },
        excludedIds: Array,
        excludedItemsWarningMessageKey: {
            type: String,
            default: 'addContainerModal.errors.resultsAmongExcludedIds'
        },
    },
    template: require('./add-container-form.html'),
})
export class AddContainerForm extends ReactiveVue {
    public onSubmit: Config['onSubmit']
    public initialValues: Values
    public container: Container = null as any
    public movementType: MovementTypes
    public canChainAdd: boolean
    public statusesToSearch: ContainerStatus[] | undefined
    public excludedIds: Array<string>
    private excludedItemsWarningMessageKey: string

    public containerSelectors = containerSelectors

    public MovementTypes = MovementTypes

    public $refs: {
        barcodeReader: BarcodeReader
        form: Form
        location: Field
    }

    private $containerDataStore: ContainerDataStore
    private searchTextValue$ = new Subject()
    private searchText: string = ''
    private pending: boolean = true
    private readonly pageSize: number = 15
    private containers: Container[] = []
    private isBarcodeReaderActive: boolean = false
    private maxItemNumber = 0
    private currentPageIndex: number = 0
    private errorMessage: String = ''


    private selectedItemIndex = -1

    private readonly INPUT_FILTER_IDENTIFIER: string = 'add-item-search-query-input'

    // Icons
    private loadingIcon = faCircleNotch
    private barcodeIcon = faBarcode
    public openLockIcon = faLockOpen
    public closedLockIcon = faLock

    private subscribeToContainers(searchText = '', pageNumber = 0) {
        this.unsubscribeFrom('containers-subscription')
        this.pending = true

        let search$ = this.$containerDataStore.searchAll(
            {
                queryText: searchText,
                pageSize: this.pageSize,
                page: this.currentPageIndex,
            },
            undefined,
            undefined,
            undefined,
            undefined,
            true,
        )
        if(this.statusesToSearch) {
            search$ = this.$containerDataStore.searchAmongStatusList(
                    searchText,
                    this.pageSize,
                    this.currentPageIndex,
                    this.statusesToSearch,
                    undefined,
                    [],
                )
        }

        this.subscribeTo(
            search$
                .pipe(
                    first(),
                    map(result => {
                        let excludedContainersThatMatched = [] as Container[] // used to inform user that the container is already in the movement/container

                        const filteredContainers = result.allData.filter(container => {
                            if(this.excludedIds.includes(container.id)) {
                                excludedContainersThatMatched.push(container)
                                return false
                            } else {
                                return true
                            }
                        })

                        return {
                            totalSize: filteredContainers.length,
                            allData: filteredContainers,
                            excludedContainersThatMatched
                        }
                    }),
                ),
            (searchResponse) => {
                this.maxItemNumber = searchResponse.totalSize
                this.containers = searchResponse.allData
                this.pending = false

                if(searchResponse.excludedContainersThatMatched && searchResponse.excludedContainersThatMatched.length) {
                    this.errorMessage = this.$t(this.excludedItemsWarningMessageKey) as string
                } else {
                    this.errorMessage = ''
                }
            },
            'containers-subscription',
        )
    }

    private incrementPageIndex() {
        this.currentPageIndex =
            this.currentPageIndex + 1 > this.lastPageIndex
                ? this.lastPageIndex
                : this.currentPageIndex + 1
        this.unsubscribeFrom('containers-subscription')
        this.subscribeToContainers()
    }

    private decrementPageIndex() {
        this.currentPageIndex = this.currentPageIndex - 1 < 0 ? 0 : this.currentPageIndex - 1
        this.unsubscribeFrom('containers-subscription')
        this.subscribeToContainers()
    }

    @Watch('searchText')
    private onSearchTextChange() {
        this.searchTextValue$.next(this.searchText)
    }

    private get lastPageIndex() {
        return Math.ceil(this.maxItemNumber / this.pageSize)
    }

    private toggleBarcodeReader() {
        this.isBarcodeReaderActive = !this.isBarcodeReaderActive
    }

    private onBarcodeReaderInput(value: string) {
        this.searchText = value
        this.isBarcodeReaderActive = false
    }

    private onItemSelected(container, index) {
        this.$emit('container-selected', { id: container.id, type: container.type })
    }
}
